import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { ColumnChooserModel } from '../../models/common/ColumnChooserModel'
import { ColumnFilterModel } from '../../models/common/ColumnFilterModel'
import ColumnFilterPopover from './ColumnFilterPopover'
import {
    COLUMN_FILTER_DEBOUNCE_MS,
    COLUMN_FILTER_EMPTY_LABEL,
    COLUMN_FILTER_EMPTY_VALUE,
    ColumnFilterDefinition,
    ColumnFilterDefinitions,
    ColumnFilterOption,
    columnFilterSummary,
    foldText,
    isColumnFilterActive
} from './ColumnFilterTypes'

/**
 * Liste tablosunda başlık satırının altındaki filtre satırı.
 *
 * Kullanımı (thead içinde, başlık satırından hemen sonra):
 *
 *   <ColumnFilterRow
 *       columns={gorunenKolonlar}
 *       definitions={kolonFiltreleri}
 *       filters={filter.columnFilters}
 *       onChange={(cf) => getData({ ...filter, columnFilters: cf, currentPage: 1, skip: 0 })}
 *       leadingCellCount={1}
 *   />
 *
 * Kolon adları backend'deki filtre eşlemesiyle (FilterMap) aynı olmalıdır.
 * Filtreler sunucuda uygulanır; bu bileşen yalnızca ne istendiğini toplar.
 */
interface Props {
    /** Görünen kolonlar, ekrandaki sırasıyla. */
    columns: ColumnChooserModel[]
    definitions: ColumnFilterDefinitions
    filters: ColumnFilterModel[] | undefined
    onChange: (filters: ColumnFilterModel[]) => void
    /** Kolonlardan önce gelen hücre sayısı (ör. seçim kutusu kolonu). */
    leadingCellCount?: number
}

const ColumnFilterRow = ({ columns, definitions, filters, onChange, leadingCellCount = 0 }: Props) => {
    const rowRef = useRef<HTMLTableRowElement>(null)
    const [top, setTop] = useState(0)
    const [optionCache, setOptionCache] = useState<Record<string, ColumnFilterOption[]>>({})

    // Kolon genişlikleri yalnızca büyür, süzünce daralmaz: aksi halde kısa değerli
    // sayfalarda kolonlar daralıp tablo sağa sola kayıyor, aranan kolon gözden kayboluyordu.
    // Görünen kolonlar değişince (Kolon Seçici) ölçü sıfırdan başlar.
    const widths = useRef<Record<string, number>>({})
    const [, setWidthVersion] = useState(0)
    const columnsKey = columns.map(x => x.name).join('|')

    useEffect(() => {
        widths.current = {}
        setWidthVersion(v => v + 1)
    }, [columnsKey])

    useLayoutEffect(() => {
        const cells = rowRef.current?.children

        if (!cells)
            return

        let changed = false

        columns.forEach((column, i) => {
            const cell = cells[leadingCellCount + i] as HTMLElement | undefined

            if (!cell)
                return

            const w = Math.ceil(cell.getBoundingClientRect().width)

            // 2 px pay: kenarlık yuvarlamaları ölçüyü her çizimde büyütmesin.
            if (w > (widths.current[column.name] ?? 0) + 2) {
                widths.current[column.name] = w
                changed = true
            }
        })

        if (changed)
            setWidthVersion(v => v + 1)
    })

    // Gecikmeli (yazmayı bırakınca) uygulamada eski değerlerle istek gitmesin diye
    // en güncel filtreler ve geri çağırma burada tutulur.
    const latest = useRef({ filters: filters ?? [], onChange })
    latest.current = { filters: filters ?? [], onChange }

    // Başlık satırı yapışkan; filtre satırı onun hemen altına yapışır.
    useLayoutEffect(() => {
        const header = rowRef.current?.previousElementSibling as HTMLElement | null
        const h = header ? header.offsetHeight : 0

        if (h != top)
            setTop(h)
    })

    const apply = useCallback((column: string, next: ColumnFilterModel | undefined) => {
        const current = latest.current.filters
        const rest = current.filter(x => x.column != column)
        const result = next && isColumnFilterActive(next) ? [...rest, { ...next, column }] : rest

        // Değişiklik yoksa istek atılmaz.
        if (JSON.stringify(result) == JSON.stringify(current))
            return

        latest.current.onChange(result)
    }, [])

    const findFilter = (column: string) => (filters ?? []).find(x => x.column == column)

    return (
        <tr ref={rowRef} className='kf-row'>
            {
                Array.from({ length: leadingCellCount }).map((_, i) =>
                    <th key={'lead-' + i} style={{ top: top }}></th>
                )
            }

            {
                columns.map((column) => {
                    const definition = definitions[column.name]

                    return (
                        <th key={column.name} style={{ top: top, minWidth: widths.current[column.name] }}>
                            {
                                definition && (
                                    <ColumnFilterCell
                                        column={column}
                                        definition={definition}
                                        filter={findFilter(column.name)}
                                        apply={apply}
                                        options={optionCache[column.name]}
                                        setOptions={(list) => setOptionCache(c => ({ ...c, [column.name]: list }))}
                                    />
                                )
                            }
                        </th>
                    )
                })
            }
        </tr>
    )
}

interface CellProps {
    column: ColumnChooserModel
    definition: ColumnFilterDefinition
    filter: ColumnFilterModel | undefined
    apply: (column: string, next: ColumnFilterModel | undefined) => void
    options: ColumnFilterOption[] | undefined
    setOptions: (list: ColumnFilterOption[]) => void
}

const ColumnFilterCell = (props: CellProps) => {
    if (props.definition.type == 'text')
        return <TextFilter {...props} />

    return <PopoverFilter {...props} />
}

// --- Metin -------------------------------------------------------------------

const TextFilter = ({ column, filter, apply }: CellProps) => {
    const applied = filter?.text ?? ''
    const [text, setText] = useState(applied)
    const timer = useRef<number | undefined>(undefined)

    // Filtre dışarıdan (etiketten ya da "Tümünü temizle"den) kaldırılırsa kutu da boşalır.
    useEffect(() => { setText(applied) }, [applied])

    useEffect(() => () => window.clearTimeout(timer.current), [])

    const commit = (value: string) => {
        window.clearTimeout(timer.current)
        apply(column.name, value.trim() ? { column: column.name, text: value.trim() } : undefined)
    }

    return (
        <div className='kf-text'>
            <input
                id={'kf-' + column.name}
                className={'kf-input' + (applied ? ' kf-active' : '')}
                type='text'
                autoComplete='off'
                placeholder='Ara'
                title={column.title + ' içinde ara (Enter ile hemen)'}
                value={text}
                onChange={(e) => {
                    const value = e.target.value
                    setText(value)
                    window.clearTimeout(timer.current)
                    timer.current = window.setTimeout(() => commit(value), COLUMN_FILTER_DEBOUNCE_MS)
                }}
                onKeyDown={(e) => {
                    if (e.key == 'Enter') {
                        e.preventDefault()
                        commit(text)
                    }
                    else if (e.key == 'Escape' && text) {
                        setText('')
                        commit('')
                    }
                }}
            />
            {
                text && (
                    <button
                        type='button'
                        className='kf-clear'
                        title='Temizle'
                        onClick={() => { setText(''); commit('') }}
                    >×</button>
                )
            }
        </div>
    )
}

// --- Seçim, tarih, sayı (açılır panelli) ----------------------------------------

const PopoverFilter = (props: CellProps) => {
    const { column, definition, filter } = props
    const buttonRef = useRef<HTMLButtonElement>(null)
    const [open, setOpen] = useState(false)
    const close = useCallback(() => setOpen(false), [])

    const active = isColumnFilterActive(filter)
    const placeholder = definition.type == 'select' ? 'Tümü' : 'Aralık'

    return (
        <React.Fragment>
            <button
                ref={buttonRef}
                id={'kf-' + column.name}
                type='button'
                className={'kf-trigger' + (active ? ' kf-active' : '')}
                title={active ? column.title + ': ' + columnFilterSummary(definition.type, filter) : column.title + ' filtresi'}
                onClick={() => setOpen(!open)}
            >
                <span className={active ? '' : 'kf-placeholder'}>
                    {active ? columnFilterSummary(definition.type, filter) : placeholder}
                </span>
            </button>

            {
                open && buttonRef.current && (
                    <ColumnFilterPopover anchor={buttonRef.current} onClose={close} width={definition.type == 'select' ? 270 : 250}>
                        {
                            definition.type == 'select'
                                ? <SelectPanel {...props} close={close} />
                                : <RangePanel {...props} close={close} />
                        }
                    </ColumnFilterPopover>
                )
            }
        </React.Fragment>
    )
}

const SelectPanel = ({ column, definition, filter, apply, options, setOptions, close }: CellProps & { close: () => void }) => {
    const [selected, setSelected] = useState<string[]>(filter?.values ?? [])
    const [search, setSearch] = useState('')
    const [loading, setLoading] = useState(options == undefined)

    useEffect(() => {
        if (options != undefined || !definition.loadOptions)
            return

        let cancelled = false

        definition.loadOptions()
            .then((list) => { if (!cancelled) setOptions(list) })
            .catch((error) => { toast.error(typeof error == 'string' ? error : 'Seçenekler yüklenemedi.') })
            .finally(() => { if (!cancelled) setLoading(false) })

        return () => { cancelled = true }
    }, [])

    const loaded = options ?? []

    // "(Boş)" en üstte: alanı doldurulmamış kayıtları da yakalamak için.
    const list = loading
        ? []
        : definition.allowEmpty === false
            ? loaded
            : [{ value: COLUMN_FILTER_EMPTY_VALUE, label: COLUMN_FILTER_EMPTY_LABEL }, ...loaded]

    // Türkçe harf farkı gözetilmez: "dagitim" yazınca "Dağıtımda" bulunur.
    const q = foldText(search.trim())
    const visible = q ? list.filter(x => foldText(x.label).includes(q)) : list
    const allVisibleSelected = visible.length > 0 && visible.every(x => selected.includes(x.value))

    const toggle = (value: string) =>
        setSelected(s => s.includes(value) ? s.filter(x => x != value) : [...s, value])

    const toggleAll = () =>
        setSelected(s => allVisibleSelected
            ? s.filter(x => !visible.some(v => v.value == x))
            : Array.from(new Set([...s, ...visible.map(x => x.value)])))

    const onApply = () => {
        apply(column.name, selected.length > 0
            ? {
                column: column.name,
                values: selected,
                valueLabels: selected.map(v => list.find(x => x.value == v)?.label ?? v)
            }
            : undefined)
        close()
    }

    return (
        <React.Fragment>
            <div className='kf-popover-head'>
                <div className='kf-popover-title'>{column.title}</div>
                <input
                    id={'kf-search-' + column.name}
                    className='kf-input'
                    type='text'
                    autoComplete='off'
                    placeholder='Listede ara'
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key == 'Enter') { e.preventDefault(); onApply() } }}
                />
            </div>

            <div className='kf-options'>
                {loading && <div className='kf-muted'>Yükleniyor…</div>}
                {!loading && loaded.length == 0 && <div className='kf-muted'>Tanımlı seçenek yok.</div>}
                {!loading && list.length > 0 && visible.length == 0 && <div className='kf-muted'>"{search}" bulunamadı.</div>}
                {
                    visible.length > 0 && (
                        <label className='kf-option kf-option-all'>
                            <input type='checkbox' className='form-check-input' checked={allVisibleSelected} onChange={toggleAll} />
                            <span>{q ? 'Bulunanların tümünü seç' : 'Tümünü seç'}</span>
                        </label>
                    )
                }
                {
                    visible.map(x =>
                        <label key={x.value} className='kf-option'>
                            <input type='checkbox' className='form-check-input' checked={selected.includes(x.value)} onChange={() => toggle(x.value)} />
                            {
                                x.value == COLUMN_FILTER_EMPTY_VALUE
                                    ? <span className='kf-empty-option'>{x.label}</span>
                                    : <span>{definition.renderOption ? definition.renderOption(x.label) : x.label}</span>
                            }
                        </label>
                    )
                }
            </div>

            <div className='kf-popover-foot'>
                <span className='kf-muted'>{selected.length > 0 ? selected.length + ' seçili' : ''}</span>
                <div className='kf-popover-buttons'>
                    <button type='button' className='btn btn-sm btn-light' onClick={() => { apply(column.name, undefined); close() }}>Temizle</button>
                    <button type='button' className='btn btn-sm btn-primary' onClick={onApply}>Uygula</button>
                </div>
            </div>
        </React.Fragment>
    )
}

// Yerel saatle yyyy-MM-dd
const isoDate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const RangePanel = ({ column, definition, filter, apply, close }: CellProps & { close: () => void }) => {
    const isDate = definition.type == 'date'
    const [from, setFrom] = useState(filter?.from ?? '')
    const [to, setTo] = useState(filter?.to ?? '')

    const onApply = () => {
        apply(column.name, from || to ? { column: column.name, from: from || undefined, to: to || undefined } : undefined)
        close()
    }

    const preset = (days: number | 'month') => {
        const today = new Date()
        const start = days == 'month' ? new Date(today.getFullYear(), today.getMonth(), 1) : new Date(today.getFullYear(), today.getMonth(), today.getDate() - days)
        setFrom(isoDate(start))
        setTo(isoDate(today))
    }

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key == 'Enter') {
            e.preventDefault()
            onApply()
        }
    }

    return (
        <React.Fragment>
            <div className='kf-popover-head'>
                <div className='kf-popover-title'>{column.title}</div>
                {
                    isDate && (
                        <div className='kf-presets'>
                            <button type='button' onClick={() => preset(0)}>Bugün</button>
                            <button type='button' onClick={() => preset(6)}>Son 7 gün</button>
                            <button type='button' onClick={() => preset('month')}>Bu ay</button>
                        </div>
                    )
                }
            </div>

            <div className='kf-range'>
                <label htmlFor={'kf-from-' + column.name}>{isDate ? 'Başlangıç' : 'En az'}</label>
                <input
                    id={'kf-from-' + column.name}
                    className='kf-input'
                    type={isDate ? 'date' : 'number'}
                    step='any'
                    autoFocus
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    onKeyDown={onKeyDown}
                />
                <label htmlFor={'kf-to-' + column.name}>{isDate ? 'Bitiş' : 'En çok'}</label>
                <input
                    id={'kf-to-' + column.name}
                    className='kf-input'
                    type={isDate ? 'date' : 'number'}
                    step='any'
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    onKeyDown={onKeyDown}
                />
            </div>

            <div className='kf-popover-foot'>
                <span className='kf-muted'>{isDate ? 'İki gün de dahil' : 'İki değer de dahil'}</span>
                <div className='kf-popover-buttons'>
                    <button type='button' className='btn btn-sm btn-light' onClick={() => { apply(column.name, undefined); close() }}>Temizle</button>
                    <button type='button' className='btn btn-sm btn-primary' onClick={onApply}>Uygula</button>
                </div>
            </div>
        </React.Fragment>
    )
}

export default ColumnFilterRow
